<script setup lang="ts">
import {ref, PropType, computed, watch} from "vue";
import {isYapiError, DashUser, YapiError} from "@YenzaCT/sdk";
import DangerConfirmationField from "@/components/DangerConfirmationField.vue";
import {useGlobalStore} from "@/store";
import yapi from "@/lib/yapi";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const props = defineProps({
  users: {
    type: Array as PropType<DashUser[]>,
    required: true
  }
});

const error = ref<string | undefined>("");
const selectedTenant = ref<string | null>();
const selectedInstitution = ref<string | null>();
const selectedCohort = ref<string | null>();
const confirmDanger = ref<boolean>(false);
const store = useGlobalStore();
const showResults = ref<boolean>(false);
const moveResults = ref<{ status: string, message: string }[]>([]);
const isSubmitting = ref<boolean>(false);

const emit = defineEmits<{
  (e: "close"): void
  (e: "success"): void
}>();

// Computed properties for result totals
const movedCount = computed(() =>
  moveResults.value.filter((result) => result.status === "moved").length);

const skippedCount = computed(() =>
  moveResults.value.filter((result) => result.status === "skipped").length);

const errorCount = computed(() =>
  moveResults.value.filter((result) => result.status === "error").length);

// Watchers to reset error when selections change
watch([selectedTenant], () => {
  selectedInstitution.value = null;
  selectedCohort.value = null;
});

watch([selectedInstitution], () => {
  selectedCohort.value = null;
});

async function moveUsers() {
  if (!selectedTenant.value || !selectedInstitution.value || !selectedCohort.value) {
    error.value = "Please select a tenant, institution, and cohort.";
    return;
  }

  try {
    isSubmitting.value = true;
    store.networkBusy = true;
    const userIds = props.users.map((user) => user._id).filter((id): id is string => id !== undefined);

    if (
      userIds && userIds.length > 0 &&
      selectedTenant.value &&
      selectedInstitution.value &&
      selectedCohort.value) {
      const results = await yapi.admin.user.moveMultiple(
        userIds,
        selectedTenant.value,
        selectedInstitution.value,
        selectedCohort.value
      );

      moveResults.value = results.data;
      showResults.value = true;
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    isSubmitting.value = false;
    store.networkBusy = false;
  }
}

</script>

<template>
  <v-card>
    <!-- Form View -->
    <div v-if="!showResults">
      <v-card-title> {{ `Move ${props.users.length} ${props.users.length === 1 ? 'User' : 'Users'}` }}</v-card-title>
      <v-card-text>
        <v-row v-if="error">
          <v-col>
            <v-alert density="compact" v-if="error" type="error" class="mb-4"> {{ error }}</v-alert>
          </v-col>
        </v-row>

        <v-row class="mb-2">
          <v-col>
            Users can only be moved to a different institution or cohort in the same tenant.
          </v-col>
        </v-row>

        <div class="bg-grey-lighten-3 pa-4 rounded mb-4">
          <v-row>
            <v-col>
              <span class="font-weight-bold">1. Select a Tenant</span>
              <tenant-search-autocomplete
                v-model="selectedTenant"
                entity-type="tenant"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <span class="font-weight-bold">2. Select an Institution</span>
              <tenant-search-autocomplete
                v-model="selectedInstitution"
                :disabled="!selectedTenant"
                entity-type="institution"
                :tenant-ids="selectedTenant ? [selectedTenant] : []"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <span class="font-weight-bold">3. Select Cohort</span>
              <tenant-search-autocomplete
                v-model="selectedCohort"
                :disabled="!selectedInstitution"
                entity-type="cohort"
                :institution-ids="selectedInstitution ? [selectedInstitution] : []"
              />
            </v-col>
          </v-row>
        </div>
        <v-row>
          <v-col>
            <danger-confirmation-field
              v-model="confirmDanger"
              message="This will permanently move the selected users to the new institution / cohort. This action cannot be undone."
              confirm-string=""
              action-label="Confirm"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          variant="outlined"
          @click="emit('close')"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="moveUsers()"
          :disabled="!confirmDanger || !selectedTenant || !selectedInstitution || !selectedCohort || isSubmitting"
          :loading="isSubmitting"
        >
          Confirm Move
        </v-btn>
      </v-card-actions>
    </div>

    <!-- Results View -->
    <div v-else>
      <v-card-title>Move Results</v-card-title>
      <v-card-text>
        <!-- Summary Cards -->
        <v-row class="mb-4">
          <v-col cols="4" v-if="movedCount > 0">
            <v-card color="success" variant="tonal" class="pa-3 text-center">
              <div class="text-h4">{{ movedCount }}</div>
              <div class="text-body-2">Moved</div>
            </v-card>
          </v-col>
          <v-col cols="4" v-if="skippedCount > 0">
            <v-card color="warning" variant="tonal" class="pa-3 text-center">
              <div class="text-h4">{{ skippedCount }}</div>
              <div class="text-body-2">Skipped</div>
            </v-card>
          </v-col>
          <v-col cols="4" v-if="errorCount > 0">
            <v-card color="error" variant="tonal" class="pa-3 text-center">
              <div class="text-h4">{{ errorCount }}</div>
              <div class="text-body-2">Errors</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Expandable Details -->
        <v-expansion-panels>
          <v-expansion-panel title="View Details">
            <v-expansion-panel-text>
              <v-list>
                <v-list-item
                  v-for="(result, index) in moveResults"
                  :key="index"
                  class="mb-2"
                >
                  <v-list-item-content>
                    <v-chip
                      :color="result.status === 'moved' ? 'success' : result.status === 'skipped' ? 'warning' : 'error'"
                      size="small"
                      class="mr-2"
                    >
                      {{ result.status }}
                    </v-chip>
                    {{ result.message }}
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          color="primary"
          @click="emit('success')"
        >
          Close
        </v-btn>
      </v-card-actions>
    </div>
  </v-card>
</template>

<style scoped>

</style>
